import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode
} from '../types'
import { addColumn } from '../utils/schemas'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class QuadbinKRing extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.quadbinkring',
        group: 'Spatial Indexes',
        title: 'Quadbin KRing',
        description: `
        Returns all Quadbin cell indexes and their distances in a filled square k-ring centered at
        the origin in no particular order.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'quadbin',
          type: WorkflowComponentParamType.Column,
          title: 'Quadbin index column',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          min: 1,
          default: 1,
          mode: WorkflowComponentParameterMode.Slider
        },
        {
          name: 'includecols',
          type: WorkflowComponentParamType.Boolean,
          title: 'Keep input table columns',
          default: true
        },
        {
          name: 'includedistance',
          type: WorkflowComponentParamType.Boolean,
          title: 'Include KRing distance column',
          default: true
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)
    const namespace = `${connection.quoteName(envVars.analyticsToolboxDataset as string)}`
    const sourceT = `${connection.quoteName(preparedParams.source as string)}`
    let select = ''
    let query = ''
    let includeDistance = ''
    let kringIndexClause = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        if (preparedParams.includedistance) {
          includeDistance = 'kring.distance AS kring_distance'
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `kring.index AS kring_index`
        // add ',' if no ending clause before FROM
        kringIndexClause += preparedParams.includedistance || preparedParams.includecols ? ',' : ''

        if (preparedParams.includecols) {
          select = `WITH __quadbin_kring AS
                      (
                        SELECT
                          ${namespace}.QUADBIN_KRING_DISTANCES(
                              ${preparedParams.quadbin},
                              CAST(${preparedParams.size} AS INTEGER)
                          ) quadbin_kring,
                          *
                        FROM ${sourceT}
                      )
                    SELECT
                      ${kringIndexClause}
                      ${includeDistance}
                      allvalues.* except(quadbin_kring)
                    FROM
                      __quadbin_kring allvalues,
                      unnest(allvalues.quadbin_kring) kring`
        } else {
          select = `WITH __quadbin_kring AS
                      (
                        SELECT
                          ${preparedParams.quadbin},
                          ${namespace}.QUADBIN_KRING_DISTANCES(
                              ${preparedParams.quadbin},
                              CAST(${preparedParams.size} AS INTEGER)
                          ) quadbin_kring
                        FROM ${sourceT}
                      )
                    SELECT
                      allvalues.${preparedParams.quadbin},
                      ${kringIndexClause}
                      ${includeDistance}
                    FROM
                      __quadbin_kring allvalues,
                      unnest(allvalues.quadbin_kring) kring`
        }
        break
      }
      case Provider.Snowflake: {
        if (preparedParams.includedistance) {
          includeDistance = `TO_NUMBER(JSON_EXTRACT_PATH_TEXT(new.value, 'distance')) as kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `TO_NUMBER(JSON_EXTRACT_PATH_TEXT(new.value, 'index')) as kring_index`
        // add ',' if no ending claquse before FROM
        kringIndexClause += preparedParams.includedistance || preparedParams.includecols ? ',' : ''

        if (preparedParams.includecols) {
          select = `WITH __quadbin_kring AS
                    (
                      SELECT
                        ${preparedParams.quadbin},
                        ${namespace}.QUADBIN_KRING_DISTANCES(
                          ${preparedParams.quadbin},
                          CAST(${preparedParams.size} AS INTEGER)
                        ) quadbin_kring
                      FROM ${sourceT}
                    )
                  SELECT
                    source.${preparedParams.quadbin},
                    ${kringIndexClause}
                    ${includeDistance}
                    source.* EXCLUDE(${preparedParams.quadbin})
                  FROM
                    ${sourceT} source,
                    __quadbin_kring kring,
                    lateral FLATTEN(input => kring.quadbin_kring) new
                  WHERE
                    source.${preparedParams.quadbin} = kring.${preparedParams.quadbin}`
        } else {
          select = `WITH __quadbin_kring AS
                    (
                      SELECT
                        ${preparedParams.quadbin},
                        ${namespace}.QUADBIN_KRING_DISTANCES(
                          ${preparedParams.quadbin},
                          CAST(${preparedParams.size} AS INTEGER)
                        ) quadbin_kring
                      FROM ${sourceT}
                    )
                  SELECT
                    kring.${preparedParams.quadbin},
                    ${kringIndexClause}
                    ${includeDistance}
                  FROM
                    __quadbin_kring kring,
                    lateral FLATTEN(input => kring.quadbin_kring) new`
        }
        break
      }
      case Provider.Redshift: {
        if (preparedParams.includedistance) {
          includeDistance = `(kring_array_element.distance)::INTEGER AS kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }
        kringIndexClause = `(kring_array_element.index)::BIGINT AS kring_index`
        // add ',' if no ending claquse before FROM
        kringIndexClause += preparedParams.includedistance || preparedParams.includecols ? ',' : ''

        const temptable = `${tempTableName(componentName, preparedParams.source)}`
        const prepareUnnest = `CREATE TABLE ${temptable} AS
                                  SELECT
                                    ${preparedParams.quadbin},
                                    ${namespace}.QUADBIN_KRING_DISTANCES(
                                      ${preparedParams.quadbin},
                                      CAST(${preparedParams.size} AS INTEGER)
                                    ) quadbin_kring
                                  FROM ${sourceT}`
        if (preparedParams.includecols) {
          select = `SELECT
                      ${kringIndexClause}
                      ${includeDistance}
                      source.*
                    FROM
                      ${sourceT} as source,
                      ${temptable} AS kring_source,
                      kring_source.quadbin_kring AS kring_array_element
                    WHERE
                      source.${preparedParams.quadbin} = kring_source.${preparedParams.quadbin}`
        } else {
          select = `SELECT
                      kring_source.quadbin,
                      ${kringIndexClause}
                      ${includeDistance}
                    FROM
                      ${temptable} AS kring_source,
                      kring_source.quadbin_kring AS kring_array_element`
        }

        const createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
        query = dedent`BEGIN
                        ${connection.getDropTableQuery(temptable)}
                        EXECUTE '${prepareUnnest}';
                        ${createTable}
                        DROP TABLE ${temptable};
                      END;`
        break
      }
      case Provider.Postgres:
        if (preparedParams.includedistance) {
          includeDistance = `(unnest(kring.quadbin_kring)::json->>'distance')::int as kring_distance`
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }
        kringIndexClause = `(unnest(kring.quadbin_kring)::json->>'index')::bigint as kring_index`
        // add ',' if no ending claquse before FROM
        kringIndexClause += preparedParams.includedistance || preparedParams.includecols ? ',' : ''

        if (preparedParams.includecols) {
          select = `WITH __quadbin_kring AS
                      (
                        SELECT
                          ${preparedParams.quadbin},
                          ${namespace}.QUADBIN_KRING_DISTANCES(
                            ${preparedParams.quadbin},
                            CAST(${preparedParams.size} AS INTEGER)
                          ) quadbin_kring
                        FROM ${sourceT}
                      )
                    SELECT
                      ${kringIndexClause}
                      ${includeDistance}
                      source.*
                    FROM
                      ${sourceT} source,
                      __quadbin_kring kring
                    WHERE
                      source.${preparedParams.quadbin} = kring.${preparedParams.quadbin}`
        } else {
          select = `WITH __quadbin_kring AS
                      (
                        SELECT
                          ${preparedParams.quadbin},
                          ${namespace}.QUADBIN_KRING_DISTANCES(
                            ${preparedParams.quadbin},
                            CAST(${preparedParams.size} AS INTEGER)
                          ) quadbin_kring
                        FROM ${sourceT}
                      )
                    SELECT
                      ${preparedParams.quadbin},
                      ${kringIndexClause}
                      ${includeDistance}
                    FROM
                      __quadbin_kring kring`
        }
        break
      case Provider.DatabricksRest:
        if (preparedParams.includedistance) {
          includeDistance = 'kring.distance AS kring_distance'
          if (preparedParams.includecols) {
            // adding "," because the clause is not at the end of SELECT clause
            includeDistance = includeDistance + ','
          }
        }

        kringIndexClause = `kring.quadbin AS kring_index`
        // add ',' if no ending clause before FROM
        kringIndexClause += preparedParams.includedistance || preparedParams.includecols ? ',' : ''

        if (preparedParams.includecols) {
          select = `WITH __quadbin_kring AS
                        (
                          SELECT /*+ REPARTITION(40) */
                            EXPLODE(QUADBIN_KRING_DISTANCES(
                                ${preparedParams.quadbin},
                                CAST(${preparedParams.size} AS INTEGER)
                            )) kring,
                            *
                          FROM ${sourceT}
                        )
                      SELECT /*+ REPARTITION(40) */
                        ${kringIndexClause}
                        ${includeDistance}
                        * EXCEPT(kring)
                      FROM
                        __quadbin_kring`
        } else {
          select = `WITH __quadbin_kring AS
                        (
                          SELECT /*+ REPARTITION(40) */
                            ${preparedParams.quadbin},
                            EXPLODE(QUADBIN_KRING_DISTANCES(
                                ${preparedParams.quadbin},
                                CAST(${preparedParams.size} AS INTEGER)
                            )) kring
                          FROM ${sourceT}
                        )
                      SELECT /*+ REPARTITION(40) */
                        ${preparedParams.quadbin},
                        ${kringIndexClause}
                        ${includeDistance}
                      FROM
                        __quadbin_kring`
        }
        break
    }

    // generate query BUT not in case of redshift where the query has been
    // already generated before due to manage unnesting
    if (connection.type !== Provider.Redshift) {
      query = connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    }
    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    switch (provider) {
      case Provider.Postgres:
        return '1.1.1'
      default:
        return '1.0.0'
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    let inputColumns = columns.source
    if (!params.includecols) {
      inputColumns = []
    }
    if (params.includedistance) {
      inputColumns = addColumn(inputColumns, {
        name: 'kring_distance',
        type: ColumnDataType.Number
      })
    }
    return addColumn(inputColumns, {
      name: 'kring_index',
      type: ColumnDataType.Number
    })
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.Postgres, Provider.DatabricksRest]
  }
}

export { QuadbinKRing }
